<script setup lang="ts">
  import { useToast } from '@/components/ui/toast/use-toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { ChatPreferencesService } from '@/types/generated/services.gen'
  import type { PreferenceSuggestionData } from '@/types/generated/types.gen'
  import { Check, ChevronDown, ChevronRight, Lock, X } from 'lucide-vue-next'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { computed, ref, watch } from 'vue'

  const { t } = useTranslation({
    en: {
      saveAsPreference: 'Save as a preference for future chats?',
      personalPreference: 'Personal preference',
      sharedPreference: 'Shared preference',
      edit: 'Edit',
      save: 'Save',
      saving: 'Saving',
      title: 'Title',
      enterTitle: 'Enter title',
      content: 'Content',
      enterContent: 'Enter content',
      revertEdits: 'Revert',
      preferenceSaved: 'Preference saved',
      view: 'View',
      suggestionDismissed: 'Preference suggestion dismissed',
      errorSaving: 'Error saving preference',
      errorDismissing: 'Error dismissing suggestion',
      savedDescription: 'Your preference has been saved successfully',
      dismissSuggestion: 'Dismiss suggestion'
    },
    de: {
      saveAsPreference: 'Als Präferenz für zukünftige Chats speichern?',
      personalPreference: 'Persönliche Präferenz',
      sharedPreference: 'Gemeinsame Präferenz',
      edit: 'Bearbeiten',
      save: 'Speichern',
      saving: 'Speichern',
      title: 'Titel',
      enterTitle: 'Titel eingeben',
      content: 'Inhalt',
      enterContent: 'Inhalt eingeben',
      revertEdits: 'Zurücksetzen',
      preferenceSaved: 'Präferenz gespeichert',
      view: 'Ansehen',
      suggestionDismissed: 'Präferenz-Vorschlag verworfen',
      errorSaving: 'Fehler beim Speichern der Präferenz',
      errorDismissing: 'Fehler beim Verwerfen des Vorschlags',
      savedDescription: 'Ihre Präferenz wurde erfolgreich gespeichert',
      dismissSuggestion: 'Vorschlag verwerfen'
    },
    fr: {
      saveAsPreference: 'Enregistrer comme préférence pour les chats futurs ?',
      personalPreference: 'Préférence personnelle',
      sharedPreference: 'Préférence partagée',
      edit: 'Modifier',
      save: 'Enregistrer',
      saving: 'Enregistrement',
      title: 'Titre',
      enterTitle: 'Entrer le titre',
      content: 'Contenu',
      enterContent: 'Entrer le contenu',
      revertEdits: 'Annuler',
      preferenceSaved: 'Préférence enregistrée',
      view: 'Voir',
      suggestionDismissed: 'Suggestion de préférence rejetée',
      errorSaving: "Erreur lors de l'enregistrement de la préférence",
      errorDismissing: 'Erreur lors du rejet de la suggestion',
      savedDescription: 'Votre préférence a été enregistrée avec succès',
      dismissSuggestion: 'Rejeter la suggestion'
    }
  })

  const props = defineProps<{
    suggestion: PreferenceSuggestionData
  }>()

  type SaveSuggestionResponse = Awaited<
    ReturnType<typeof ChatPreferencesService.chatPreferencesSaveSuggestionCreate>
  >

  type PreferenceScope = 'USER' | 'ORGANIZATION'

  const normalizeScope = (scope?: string | null): PreferenceScope =>
    scope === 'ORGANIZATION' ? 'ORGANIZATION' : 'USER'

  const emit = defineEmits<{
    dismissed: []
    saved: [preference: SaveSuggestionResponse]
    viewInLibrary: []
  }>()

  const { toast } = useToast()

  // --- State ---
  const getInitialState = () => {
    switch (props.suggestion.state) {
      case 'ACCEPTED':
        return 'saved' as const
      case 'DISMISSED':
        return 'dismissed' as const
      default:
        return 'suggestion' as const
    }
  }

  const currentState = ref<'suggestion' | 'editing' | 'saved' | 'dismissed'>(getInitialState())
  const isSaving = ref(false)
  const isDismissing = ref(false)
  const scopePopoverOpen = ref(false)

  // --- Edit form ---
  const editForm = ref({
    title: props.suggestion.title,
    content: props.suggestion.content,
    scope: normalizeScope(props.suggestion.suggested_scope)
  })

  watch(
    () => props.suggestion,
    () => {
      currentState.value = getInitialState()
      editForm.value = {
        title: props.suggestion.title,
        content: props.suggestion.content,
        scope: normalizeScope(props.suggestion.suggested_scope)
      }
    },
    { deep: true }
  )

  const isFormValid = computed(
    () => editForm.value.title.trim().length > 0 && editForm.value.content.trim().length > 0
  )

  const scopeLabel = computed(() =>
    editForm.value.scope === 'ORGANIZATION' ? t('sharedPreference') : t('personalPreference')
  )

  // --- Actions ---
  function startEditing() {
    currentState.value = 'editing'
  }

  function revertEdits() {
    editForm.value = {
      title: props.suggestion.title,
      content: props.suggestion.content,
      scope: normalizeScope(props.suggestion.suggested_scope)
    }
    currentState.value = 'suggestion'
  }

  function selectScope(scope: PreferenceScope) {
    editForm.value.scope = scope
    scopePopoverOpen.value = false
  }

  async function saveSuggestion() {
    if (!isFormValid.value || isSaving.value) return

    isSaving.value = true
    try {
      const response = await ChatPreferencesService.chatPreferencesSaveSuggestionCreate({
        requestBody: {
          title: editForm.value.title,
          content: editForm.value.content,
          scope: editForm.value.scope,
          source: 'AI_CHAT',
          suggestion_id: props.suggestion.id
        }
      })

      currentState.value = 'saved'
      toast({ title: t('preferenceSaved'), description: t('savedDescription') })
      emit('saved', response)
    } catch {
      toast({ title: t('errorSaving'), variant: 'destructive' })
    } finally {
      isSaving.value = false
    }
  }

  async function dismissSuggestion() {
    if (isDismissing.value || isSaving.value) return
    isDismissing.value = true
    try {
      await ChatPreferencesService.chatPreferencesDismissSuggestionCreate({
        requestBody: { suggestion_id: props.suggestion.id }
      })
      currentState.value = 'dismissed'
      emit('dismissed')
    } catch {
      toast({ title: t('errorDismissing'), variant: 'destructive' })
    } finally {
      isDismissing.value = false
    }
  }

  function viewInLibrary() {
    emit('viewInLibrary')
  }
</script>

<template>
  <!-- Suggestion / Editing state -->
  <div
    v-if="currentState === 'suggestion' || currentState === 'editing'"
    class="flex flex-col items-start justify-center gap-6 self-stretch rounded-xl bg-purple-50 px-3 py-4"
  >
    <div class="flex flex-col items-start justify-start gap-3 self-stretch">
      <!-- Header: title + dismiss X -->
      <div class="inline-flex items-center gap-2.5 self-stretch">
        <span class="flex-1 text-sm font-semibold leading-5 text-gray-800 font-inter">
          {{ t('saveAsPreference') }}
        </span>
        <button
          type="button"
          :aria-label="t('dismissSuggestion')"
          :disabled="isDismissing || isSaving"
          class="rounded-3xl p-0.5 disabled:opacity-50"
          @click="dismissSuggestion"
        >
          <X class="h-5 w-5 text-gray-800" />
        </button>
      </div>

      <!-- Content (read-only in suggestion state) -->
      <template v-if="currentState === 'suggestion'">
        <p class="self-stretch text-sm font-normal leading-5 text-gray-800 font-inter">
          {{ suggestion.content }}
        </p>
      </template>

      <!-- Content (editable in editing state) -->
      <template v-else>
        <div class="flex w-full flex-col gap-2">
          <label
            for="preference-suggestion-title"
            class="sr-only"
          >
            {{ t('title') }}
          </label>
          <input
            id="preference-suggestion-title"
            v-model="editForm.title"
            type="text"
            class="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-5 text-gray-800
              font-inter placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1
              focus:ring-primary"
            :placeholder="t('enterTitle')"
          />
          <label
            for="preference-suggestion-content"
            class="sr-only"
          >
            {{ t('content') }}
          </label>
          <textarea
            id="preference-suggestion-content"
            v-model="editForm.content"
            rows="3"
            class="w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-5
              text-gray-800 font-inter placeholder:text-gray-400 focus:border-primary focus:outline-none
              focus:ring-1 focus:ring-primary"
            :placeholder="t('enterContent')"
          />
        </div>
      </template>

      <!-- Action row: scope selector | edit | save -->
      <div class="inline-flex items-start gap-3 self-stretch">
        <!-- Scope selector -->
        <Popover v-model:open="scopePopoverOpen">
          <PopoverTrigger as-child>
            <button
              type="button"
              class="flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
                shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 -outline-offset-1 outline-gray-200"
            >
              <Lock class="h-5 w-5 shrink-0 text-gray-800" />
              <span
                class="whitespace-nowrap px-1.5 text-sm font-medium leading-5 text-gray-800 font-inter"
              >
                {{ scopeLabel }}
              </span>
              <ChevronDown class="h-5 w-5 shrink-0 text-gray-800" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            :side-offset="4"
            class="w-56 rounded-lg p-1"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
              @click="selectScope('USER')"
            >
              <Check
                v-if="editForm.scope === 'USER'"
                class="h-4 w-4 text-primary"
              />
              <span
                v-else
                class="h-4 w-4"
              />
              {{ t('personalPreference') }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
              @click="selectScope('ORGANIZATION')"
            >
              <Check
                v-if="editForm.scope === 'ORGANIZATION'"
                class="h-4 w-4 text-primary"
              />
              <span
                v-else
                class="h-4 w-4"
              />
              {{ t('sharedPreference') }}
            </button>
          </PopoverContent>
        </Popover>

        <!-- Spacer -->
        <div class="relative flex-1 self-stretch" />

        <!-- Edit / Revert button -->
        <button
          v-if="currentState === 'suggestion'"
          type="button"
          class="flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 -outline-offset-1 outline-gray-200"
          @click="startEditing"
        >
          <span class="px-1.5 text-sm font-medium leading-5 text-gray-800 font-inter">
            {{ t('edit') }}
          </span>
        </button>
        <button
          v-else
          type="button"
          class="flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 -outline-offset-1 outline-gray-200"
          @click="revertEdits"
        >
          <span class="px-1.5 text-sm font-medium leading-5 text-gray-800 font-inter">
            {{ t('revertEdits') }}
          </span>
        </button>

        <!-- Save button -->
        <button
          type="button"
          :disabled="isSaving || isDismissing || !isFormValid"
          class="flex h-8 items-center justify-center overflow-hidden rounded-md bg-primary p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] disabled:opacity-50"
          @click="saveSuggestion"
        >
          <span class="px-1.5 text-sm font-medium leading-5 text-white font-inter">
            {{ isSaving ? t('saving') : t('save') }}
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Saved compact state -->
  <div
    v-else-if="currentState === 'saved'"
    class="inline-flex flex-col items-start justify-center gap-3 self-stretch rounded-lg bg-purple-50 p-3
      shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 -outline-offset-1 outline-purple-100"
  >
    <div class="inline-flex items-center gap-1.5 self-stretch">
      <Check class="h-5 w-5 shrink-0 text-purple-700" />
      <span class="flex-1 text-sm font-medium leading-5 text-purple-700 font-inter">
        {{ t('preferenceSaved') }}
      </span>
      <button
        type="button"
        class="flex items-center gap-1"
        @click="viewInLibrary"
      >
        <span class="text-sm font-medium leading-5 text-purple-600 underline font-inter">
          {{ t('view') }}
        </span>
        <ChevronRight class="h-5 w-5 text-purple-600" />
      </button>
    </div>
  </div>
</template>
